/**
 * Layout pour le module de gestion de paie
 * Sidebar dark avec accent indigo/bleu marine — identité visuelle paie
 */

import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FileText,
  Users,
  Upload,
  Settings,
  Calculator,
  ClipboardList,
  BookOpen,
  BarChart2,
  LogOut,
  Menu,
  X,
  ChevronRight,
  List,
  Download,
  Briefcase,
  AlertCircle,
  FileSpreadsheet,
  Palette,
  Building2,
  LayoutDashboard,
  Home,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@features/auth';

interface NavItem {
  path: string;
  icon: React.ElementType;
  label: string;
  exact?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'PAIE',
    items: [
      { path: '/paie/bulletins', icon: FileText, label: 'Bulletins' },
      { path: '/paie/bulletins/masse', icon: FileText, label: 'Génération masse' },
    ],
  },
  {
    title: 'EMPLOYÉS',
    items: [
      { path: '/paie/employes', icon: Users, label: 'Liste', exact: true },
      { path: '/paie/employes/import', icon: Upload, label: 'Importer CSV' },
      { path: '/paie/employes/importer-excel', icon: FileSpreadsheet, label: 'Importer Excel' },
    ],
  },
  {
    title: 'CONFIGURATION',
    items: [
      { path: '/paie/configuration', icon: Settings, label: 'Paramètres', exact: true },
      { path: '/paie/configuration/simulateur', icon: Calculator, label: 'Simulateur' },
      { path: '/paie/configuration/baremes', icon: List, label: 'Barèmes ITS' },
      { path: '/paie/configuration/primes', icon: ClipboardList, label: 'Catalogue primes' },
      { path: '/paie/configuration/taxes', icon: AlertCircle, label: 'Taxes temporaires' },
      { path: '/paie/configuration/theme', icon: Palette, label: 'Thème & couleurs' },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { path: '/paie/configuration/organisation', icon: Building2, label: 'Organisation' },
      { path: '/paie/administration/projets', icon: Briefcase, label: 'Projets' },
    ],
  },
  {
    title: 'DÉCLARATIONS & RAPPORTS',
    items: [
      { path: '/paie/declarations', icon: ClipboardList, label: 'Déclarations' },
      { path: '/paie/rapports/livre-paie', icon: BookOpen, label: 'Livre de paie' },
      { path: '/paie/rapports/audit', icon: BarChart2, label: 'Rapport audit' },
      { path: '/paie/rapports/export-comptable', icon: Download, label: 'Export comptable' },
    ],
  },
];

const breadcrumbMap: Record<string, string> = {
  '/paie/bulletins': 'Bulletins',
  '/paie/bulletins/generer': 'Générer un bulletin',
  '/paie/bulletins/masse': 'Génération en masse',
  '/paie/employes': 'Employés',
  '/paie/employes/nouveau': 'Nouvel employé',
  '/paie/employes/import': 'Importer CSV',
  '/paie/employes/importer-excel': 'Importer Excel',
  '/paie/configuration': 'Configuration',
  '/paie/configuration/simulateur': 'Simulateur',
  '/paie/declarations': 'Déclarations',
  '/paie/rapports': 'Rapports',
  '/paie/rapports/livre-paie': 'Livre de paie',
  '/paie/rapports/audit': 'Rapport audit',
  '/paie/configuration/baremes': 'Barèmes ITS',
  '/paie/configuration/baremes/nouveau': 'Nouveau barème',
  '/paie/configuration/primes': 'Catalogue primes',
  '/paie/configuration/taxes': 'Taxes temporaires',
  '/paie/configuration/theme': 'Thème & couleurs',
  '/paie/rapports/export-comptable': 'Export comptable',
  '/paie/administration/projets': 'Projets',
  '/paie/administration/projets/': 'Projet — Détail',
};

function getBreadcrumb(pathname: string): string {
  if (breadcrumbMap[pathname]) {
    return breadcrumbMap[pathname];
  }
  // Routes dynamiques (/paie/bulletins/:id, /paie/employes/:id)
  for (const [key, label] of Object.entries(breadcrumbMap)) {
    if (pathname.startsWith(key + '/')) {
      return label + ' — Détail';
    }
  }
  return 'Paie';
}

export function PaieLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const breadcrumb = getBreadcrumb(location.pathname);

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-sidebar-bg transform transition-transform duration-200 ease-in-out
                   lg:translate-x-0 lg:static lg:inset-0 lg:sticky lg:top-0 lg:h-screen lg:self-start
                   ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header sidebar */}
          <div className="flex items-center justify-between h-16 px-6 bg-sidebar-hover border-b border-sidebar-border">
            <Link to="/paie/bulletins" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-primary flex items-center justify-center">
                <FileText className="w-4 h-4 text-content-inverted" />
              </div>
              <span className="text-lg font-bold text-white">Gestion Paie</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-sidebar-text hover:text-white transition-colors"
              aria-label="Fermer la sidebar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
            {navSections.map((section) => (
              <div key={section.title}>
                <p className="px-3 mb-2 text-xs font-semibold text-sidebar-text/60 uppercase tracking-wider">
                  {section.title}
                </p>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const active = isActive(item.path, item.exact);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                                  ${active
                                    ? 'bg-sidebar-active text-white shadow-sm'
                                    : 'text-sidebar-text hover:bg-sidebar-hover hover:text-white'
                                  }`}
                      >
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{item.label}</span>
                        {active && <ChevronRight className="w-4 h-4 ml-auto opacity-70" />}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Liens de navigation externe */}
          <div className="px-3 pb-2 border-t border-sidebar-border pt-3 space-y-1">
            <p className="px-3 mb-2 text-xs font-semibold text-sidebar-text/60 uppercase tracking-wider">
              Navigation
            </p>
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sidebar-text hover:bg-sidebar-hover hover:text-white transition-colors text-sm"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>Mon tableau de bord</span>
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sidebar-text hover:bg-sidebar-hover hover:text-white transition-colors text-sm"
              >
                <ShieldCheck className="w-4 h-4" />
                <span>Administration</span>
              </Link>
            )}
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sidebar-text hover:bg-sidebar-hover hover:text-white transition-colors text-sm"
            >
              <Home className="w-4 h-4" />
              <span>Accueil</span>
            </Link>
          </div>

          {/* User info + déconnexion */}
          <div className="p-4 border-t border-sidebar-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-content-inverted text-sm font-semibold flex-shrink-0">
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.prenom} {user?.nom}
                </p>
                <p className="text-xs text-sidebar-text truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-sidebar-text hover:text-white
                       hover:bg-sidebar-hover rounded-lg transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-surface border-b border-border flex items-center px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-content-muted hover:text-content transition-colors"
            aria-label="Ouvrir la sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 text-sm text-content-muted">
            <span className="text-content-subtle">Paie</span>
            <ChevronRight className="w-4 h-4" />
            <span className="font-medium text-content">{breadcrumb}</span>
          </div>
        </header>

        {/* Contenu page */}
        <main className="flex-1 p-6 overflow-auto bg-surface-raised">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default PaieLayout;
